import { ChevronRight, DollarSign, Layers, Ruler } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { PageHeader, Pestana, Pestanas } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Interruptor, Modal } from "../../components/ui/primitivos";
import { actualizarProducto, fetchCatalogo, fetchProducto } from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { pestanaActiva } from "./producto";
import { SeccionBases, SeccionPrecios, SeccionTamanos } from "./secciones";

/**
 * La ficha de un producto, porteada de `ProductoDetailPage.tsx` (ADR 0065).
 *
 * **Es una pantalla con pestañas y no un modal** (ADR 0064). Antes eran las
 * tres secciones apiladas en un modal ancho: la tabla de precios sola tiene
 * ocho columnas, así que las otras dos quedaban debajo del pliegue y había que
 * scrollear el modal para descubrir que existían.
 */
export function ProductoPagina() {
  const { id = "" } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const { can } = useSession();

  const detalle = useDatos(() => fetchProducto(id), id);
  const tiposTamano = useDatos(() => fetchCatalogo("tipos-tamano-logo"));
  const tiposBase = useDatos(() => fetchCatalogo("tipos-base"));
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);

  // La pestaña vive en la URL, como en `imprenta`: así se comparte un producto
  // ya abierto en Tamaños, y recargar no vuelve al principio.
  const [params, setParams] = useSearchParams();

  const puedeEditar = can("productos", "update");

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await detalle.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  if (detalle.error) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Producto" alVolver={() => navegar("/productos")} />
        <Aviso>{detalle.error}</Aviso>
      </div>
    );
  }

  const datos = detalle.datos;
  if (!datos) {
    return <p className="p-8 text-center text-neutral-500">Cargando producto…</p>;
  }

  const producto = datos.producto;

  // El detalle no trae los conteos del listado: los tamaños y las bases se
  // cuentan de las listas que ya vinieron, y los precios los manda el endpoint.
  const pestanas = [
    ...(can("precios", "read")
      ? [
          {
            clave: "precios",
            texto: "Precios",
            cantidad: datos.precios,
            icono: <DollarSign className="h-4 w-4" />,
          },
        ]
      : []),
    {
      clave: "tamanos",
      texto: "Tamaños",
      cantidad: datos.tamanos.length,
      icono: <Ruler className="h-4 w-4" />,
    },
    {
      clave: "bases",
      texto: "Bases",
      cantidad: datos.bases.length,
      icono: <Layers className="h-4 w-4" />,
    },
  ];
  const activa = pestanaActiva(
    params.get("tab"),
    pestanas.map((p) => p.clave),
  );

  // El producto de las secciones es el de la FICHA: los conteos que traía la
  // fila del listado no existen acá, y ninguna sección los mira.
  const paraSecciones = { ...producto, tamanos: 0, bases: 0, precios: datos.precios };

  return (
    <div className="space-y-6">
      {/* La miga de pan es de `ProductoDetailPage`: dice dónde estás cuando
          llegaste por un enlace y no desde el listado. */}
      <nav aria-label="Miga de pan" className="flex items-center text-neutral-500 text-sm">
        <Link to="/productos" className="hover:text-neutral-900">
          Productos
        </Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="font-medium text-neutral-900">{producto.nombre}</span>
      </nav>

      <PageHeader
        titulo={producto.nombre}
        subtitulo={
          <Etiqueta tono={producto.activo ? "ok" : "neutral"}>
            {producto.activo ? "Activo" : "Inactivo"}
          </Etiqueta>
        }
        alVolver={() => navegar("/productos")}
        acciones={
          puedeEditar ? (
            <Boton variante="secundario" onClick={() => setEditando(true)}>
              Editar
            </Boton>
          ) : undefined
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}

      {activa ? (
        <Pestanas valor={activa} alCambiar={(v) => setParams({ tab: v })} pestanas={pestanas}>
          {can("precios", "read") ? (
            <Pestana clave="precios">
              <SeccionPrecios producto={paraSecciones} />
            </Pestana>
          ) : null}
          <Pestana clave="tamanos">
            <SeccionTamanos
              producto={paraSecciones}
              detalle={datos}
              catalogo={tiposTamano.datos ?? []}
              puedeEditar={puedeEditar}
              accion={accion}
            />
          </Pestana>
          <Pestana clave="bases">
            <SeccionBases
              producto={paraSecciones}
              detalle={datos}
              catalogo={tiposBase.datos ?? []}
              puedeEditar={puedeEditar}
              accion={accion}
            />
          </Pestana>
        </Pestanas>
      ) : null}

      {editando ? (
        <ModalEditarProducto
          producto={producto}
          onCerrar={() => setEditando(false)}
          onGuardado={async () => {
            setEditando(false);
            await detalle.recargar();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Editar el nombre y el estado — el `ProductoDialog` de `imprenta`.
 *
 * **Sigue siendo un modal, y ahí `imprenta` coincide**: es un formulario de dos
 * campos, no un detalle. Lo que no existía acá era la pantalla para llamarlo:
 * `PATCH /productos/:id` acepta `nombre` desde siempre y **ninguna interfaz lo
 * mandaba**, así que un producto mal escrito no se podía corregir.
 */
function ModalEditarProducto({
  producto,
  onCerrar,
  onGuardado,
}: {
  producto: { id: string; nombre: string; activo: boolean };
  onCerrar: () => void;
  onGuardado: () => Promise<void>;
}) {
  const [nombre, setNombre] = useState(producto.nombre);
  const [activo, setActivo] = useState(producto.activo);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  return (
    <Modal abierto onCerrar={onCerrar} titulo={`Editar ${producto.nombre}`}>
      <div className="space-y-4">
        {error ? <Aviso>{error}</Aviso> : null}
        <Campo
          etiqueta="Nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej: Taza cerámica"
        />
        <div className="flex items-center gap-2 text-neutral-700 text-sm">
          <Interruptor aria-label="Activo" checked={activo} onCheckedChange={setActivo} />
          <span aria-hidden="true">Activo</span>
        </div>
        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            disabled={guardando || nombre.trim() === ""}
            onClick={async () => {
              setGuardando(true);
              setError(null);
              try {
                await actualizarProducto(producto.id, { nombre: nombre.trim(), activo });
                await onGuardado();
              } catch (err) {
                setError(err instanceof Error ? err.message : "No se pudo guardar");
              } finally {
                setGuardando(false);
              }
            }}
          >
            {guardando ? "Guardando..." : "Guardar"}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
